import {Button, Lead, Leads, Text, Trails, Typography} from '@gwc/main'
import {genericMemo} from '@logistics-frontend/utils'
import clsx from 'clsx'
import {memo} from 'react'

import {Link, RouterLink} from '../link'
import * as classes from './Button.classes'
import {getTypographyVars} from './Button.utils'
import {AllowedComponent, ButtonProps as Props, IconButtonProps, LinkButtonProps} from './types'

function Buttonv2Base<C extends AllowedComponent = 'button'>(props: Props<C>) {
  const {
    'data-testid': dataTestId,
    Component,
    fullWidth,
    className,
    size = 'm',
    textVariant: textVariantProp,
    lead,
    leadType: Lead = Leads.Icon,
    trail,
    trailType: Trail = Trails.Icon,
    children,
    variant = 'default',
    onPress,
    ...buttonProps
  } = props
  const {variant: textVariant, thickness: textThickness} = getTypographyVars({size, variant})
  const buttonVariant = variant === 'error' ? 'ghost' : variant

  return (
    // @ts-expect-error Button из GWC ожидает только нативные теги, но по факту умеет обрабатывать функции
    <Button
      className={clsx(className, {
        [classes.fullWidth]: fullWidth,
        [classes.error]: variant === 'error',
        [classes.defaultNonTransparent]: variant === 'default',
      })}
      Component={Component || 'button'}
      size={size}
      variant={buttonVariant}
      {...buttonProps}
      onPress={onPress}
      data-testid={dataTestId}
    >
      {lead && <Lead>{lead}</Lead>}
      {typeof children === 'string' ? (
        <Text
          // @ts-expect-error хак, чтобы наследовать выравнивание кнопки, а не компонента Text (в основном по центру)
          align={false}
          alignItems="center"
          // @ts-expect-error хак, чтобы наследовать цвет текста кнопки, а не компонента Text
          color={false}
          size={size}
        >
          <Typography data-testid={`typography-${dataTestId}`} thickness={textThickness} variant={textVariant}>
            {children}
          </Typography>
        </Text>
      ) : (
        children
      )}
      {trail && <Trail>{trail}</Trail>}
    </Button>
  )
}

export const Buttonv2 = genericMemo(Buttonv2Base)
Buttonv2.displayName = 'Buttonv2'

export const IconButton = memo(function IconButton<C extends AllowedComponent = 'button'>({
  children,
  ...props
}: IconButtonProps<C>) {
  return (
    <Button {...props}>
      <Lead>{children}</Lead>
    </Button>
  )
})

export const LinkButton = memo(function LinkButton({className, ...props}: LinkButtonProps<typeof Link>) {
  return <Buttonv2 Component={Link} {...props} className={clsx(classes.linkButton, className)} />
})

export const RouterLinkButton = memo(function RouterLinkButton({
  className,
  ...props
}: LinkButtonProps<typeof RouterLink>) {
  return <Buttonv2 Component={RouterLink} {...props} className={clsx(classes.linkButton, className)} />
})

export default Buttonv2
